<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { getErrorMessage } from '$lib/api/client';
  import { companiesApi } from '$lib/api/crm';
  import { route } from '$lib/state/route.svelte';
  import EmailActivityModal from './EmailActivityModal.svelte';
  import StatStrip from './StatStrip.svelte';
  import TimelineFeed from './TimelineFeed.svelte';
  import SiteCards from './SiteCards.svelte';
  import { mergeTimeline } from '$lib/utils/timeline';
  import { emailDirection as emailDirectionOf } from '$lib/utils/emailDirection';
  import type { Activity } from '$lib/types';

  // The owner-kind body of CompanyDetail (#/owners/<id>, #/companies/<id> for
  // a kind=owner row) — same structure and components as SiteDashboard's
  // full-page view (stat strip, unified filterable timeline) plus the sites
  // grid SitesPage already uses, since an owner's "site" IS what SitesPage
  // lists. Self-contained (owns its own rollup query) rather than threading
  // rollup data down from CompanyDetail, the same way SiteDashboard owns its
  // own queries.
  const {
    ownerId,
    onOpenSite
  }: {
    ownerId: number;
    onOpenSite: (siteId: number) => void;
  } = $props();

  const rollupQuery = createQuery(() => ({
    queryKey: ['company-rollup', ownerId],
    queryFn: () => companiesApi.rollup(ownerId)
  }));

  // ponytail: no people list is fetched for the rollup (an owner's own
  // related.people is normally empty — it's the sites that carry contacts),
  // so this always falls through to 'sent' — the rollup's own point is
  // attribution-by-site, not by-contact. Upgrade path: same as
  // SiteDashboard's, resolve from the synced mailbox's own address once
  // that's threaded through the API.
  function emailDirection(a: Activity): 'received' | 'sent' {
    return emailDirectionOf(a, []);
  }

  // Owner is always this page's own owner, so it's always redundant on every
  // card here. Brand is only redundant when every one of the owner's sites
  // shares it — some owners run multiple brands, and that's worth keeping.
  const sitesShareBrand = $derived.by(() => {
    const sites = rollupQuery.data?.sites ?? [];
    return sites.length > 0 && sites.every((s) => s.brand_name === sites[0].brand_name);
  });

  const timeline = $derived(mergeTimeline(rollupQuery.data?.activities ?? [], rollupQuery.data?.issues ?? []));
  const touchpointsCount = $derived((rollupQuery.data?.activities ?? []).length);
  const lastContactAt = $derived(timeline[0]?.created_at ?? null);

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  }

  function openIssue(issueId: number) {
    route.push({ tab: 'issues', id: issueId });
  }

  let emailDetailRow = $state<Activity | null>(null);
</script>

{#if rollupQuery.isLoading}
  <p class="tym-loading-state">Loading sites&hellip;</p>
{:else if rollupQuery.isError}
  <p class="tym-error-state">{getErrorMessage(rollupQuery.error)}</p>
{:else if rollupQuery.data}
  {@const agg = rollupQuery.data.aggregates}

  <StatStrip
    stats={[
      { label: 'Sites', value: agg.site_count },
      { label: 'Open issues', value: agg.open_issues },
      { label: 'Deals', value: agg.deal_count },
      { label: 'Touchpoints', value: touchpointsCount },
      { label: 'Last contact', value: lastContactAt ? timeAgo(lastContactAt) : '—' }
    ]}
  />

  <div class="mb-5">
    <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">
      Sites ({agg.site_count})
    </div>
    <SiteCards
      rows={rollupQuery.data.sites}
      isHotels={true}
      onOpen={onOpenSite}
      showOwner={false}
      showBrand={!sitesShareBrand}
    />
  </div>

  <div class="flex min-w-0 flex-col gap-2.5">
    <TimelineFeed
      entries={timeline}
      {emailDirection}
      onOpenIssue={openIssue}
      onOpenEmail={(a) => (emailDetailRow = a)}
      showSiteChips={true}
      onOpenSite={onOpenSite}
      emptyLabel="Nothing logged across this owner's sites yet."
    />
  </div>
{/if}

{#if emailDetailRow}
  <EmailActivityModal
    activity={emailDetailRow}
    onClose={() => (emailDetailRow = null)}
    onDismissed={() => (emailDetailRow = null)}
  />
{/if}
